import React, { useEffect, useState, useMemo } from 'react'
import { FlatList, TouchableOpacity, View } from 'react-native'
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  createStyles,
  ButtonGroup,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useHistory } from 'react-router-dom'
import { Close, ChevronRightRounded } from '@mui/icons-material'
import { useStores } from '@trivie/core'
import {
  color,
  iconArrowFullDown,
  iconCancel,
  iconPhoto,
  iconQuiz,
  iconTopic,
  iconVideoTalk,
  spacing,
  SvgIcon,
  Text,
} from '@trivie/ui-mobile'
import { TextField } from '@trivie/ui-web/components/text-field'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { FileInput } from '@trivie/ui-web/components/file-input'

import { useMediaQuery } from '@mui/material'
import { toJS } from 'mobx'

export interface CreatePostProps {
  open: boolean
  onClose: any
  selectedTopicId?: number
}

const useStyles = makeStyles(() =>
  createStyles({
    postButton: {
      '&:focus': {
        color: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'rgba(0, 0, 0, 0.87)',
      },
    },
  }),
)

export const CreatePostDialog = observer((props: CreatePostProps) => {
  const { open, onClose, selectedTopicId } = props
  const { libraryStore, userStore, talkStore, formStore, resourceStore } = useStores()
  const { topics } = libraryStore
  const { formData, setFormData } = formStore

  const { refreshPosts } = talkStore
  const { user, checkNotifications } = userStore

  const [choosingTopic, setChoosingTopic] = useState(false)
  const [onFocusUpload, setOnFocusUpload] = useState(false)
  const [disableSelectTopic, setDisableSelectTopic] = useState(false)
  const { control, setValue, reset, watch } = useForm()
  const history = useHistory()

  const handleClose = () => {
    setFormData({ photos: [] })
    onClose()
  }

  useEffect(() => {
    setFormData({ photos: [] })
    if (selectedTopicId) {
      setDisableSelectTopic(true)
      const t = topics.filter((f) => f.topic.topic_id == selectedTopicId)[0]
      selectTopic(t)
    } else {
      const t = topics.filter((f) => f.topic.title == 'General')[0]
      selectTopic(t)
    }

    return () => {
      setUploadingPhoto(false)
      setUploadingVideo(false)
    }
  }, [])

  const createForm = (data: any, postID: number) => {
    const form = new FormData()
    form.append('source_file', data)
    form.append('related_object_id', String(postID))
    form.append('related_object_type', 'Post')
    form.append('title', data.filename ? data.filename : data.name)
    return form
  }

  const createPost = (data: any) => {
    if (user) {
      talkStore.createPost(user.id, data).then((id: any) => {
        checkNotifications(user.id)
        if (formData.photos?.length > 0) {
          formData.photos.forEach((photo: any, index: number) => {
            const photoToUpload = createForm(photo, id)
            // upload each photo
            resourceStore.uploadResource(photoToUpload).then(() => {
              if (index === formData.photos.length - 1) {
                refreshPosts()
                handleClose()
              }
            })
          })
        } else if (formStore.video) {
          const videoToUpload = createForm(formStore.video, id)
          resourceStore.uploadResource(videoToUpload).then(() => {
            refreshPosts()
            handleClose()
          })
        } else {
          refreshPosts()
          handleClose()
        }
      })
    }
    reset()
  }

  const selectTopic = (topic: any) => {
    setValue('topic', topic)
    setFormData({ ...formData, topic: toJS(topic) })
    setChoosingTopic(false)
  }

  const { media } = watch()

  const { t } = useTranslation()

  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)

  const removePhoto = (photoIdx: number) => {
    const photoArray = [...formData.photos]
    photoArray.splice(photoIdx, 1)
    setFormData({ ...formData, photos: photoArray })
  }

  const isMobile = !useMediaQuery('(min-width:800px)')

  const classes = useStyles()

  const renderUploadedVideo = useMemo(() => {
    if (formStore.video) {
      return (
        <div style={{ position: 'relative', flexDirection: 'column', display: 'flex' }}>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Set video"
            onPress={() => formStore.setVideo(false)}
            style={{ position: 'absolute', zIndex: 999, top: -5 }}
          >
            <SvgIcon src={iconCancel} color="#000" />
          </TouchableOpacity>
          <video
            autoPlay={false}
            height={300}
            playsInline
            style={{ alignSelf: 'center', marginTop: 20, marginBottom: 40 }}
            disablePictureInPicture
            controls
            muted
            src={URL.createObjectURL(formStore.video)}
          />
        </div>
      )
    }
  }, [formStore.video])
  return (
    <Dialog
      sx={{
        overflow: 'hidden',
        '.MuiPaper-root': {
          width: '100%',
        },
      }}
      aria-labelledby="confirmation-dialog-title"
      open={open}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: 20,
          paddingRight: 10,
          paddingTop: 20,
          paddingBottom: 10,
        }}
      >
        <Text
          variant="label"
          tx={
            uploadingPhoto
              ? 'Discuss.uploadPhoto'
              : uploadingVideo
                ? 'Discuss.uploadVideo'
                : choosingTopic
                  ? 'Discuss.chooseTopic'
                  : 'Discuss.create'
          }
        />
        <ButtonGroup color="primary">
          <IconButton
            role="button"
            aria-label={uploadingPhoto ? 'Upload photo' : uploadingVideo ? 'Upload video' : ''}
            onClick={(e) => {
              e.stopPropagation()

              if (choosingTopic || uploadingPhoto || uploadingVideo) {
                setChoosingTopic(false)
                setUploadingPhoto(false)
                setUploadingVideo(false)
              } else {
                handleClose()
              }
            }}
          >
            <Close htmlColor={color.black} style={{ fontSize: 22 }} />
          </IconButton>
        </ButtonGroup>
      </div>

      <DialogContent
        style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 0, height: 500, paddingBottom: 40 }}
      >
        <View style={{ flex: 1, justifyContent: 'space-between' }}>
          {choosingTopic ? (
            <FlatList
              data={topics}
              ItemSeparatorComponent={() => (
                <View style={{ height: 1, backgroundColor: color.shade30, width: '100%' }} />
              )}
              renderItem={({ item }: { item: any }) => {
                return (
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel="Select topic"
                    onPress={() => {
                      selectTopic(item)
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <SvgIcon src={iconQuiz} color={color.black} size={24} />
                    <View
                      style={{
                        justifyContent: 'space-between',
                        flex: 1,
                        flexDirection: 'row',
                        paddingVertical: 15,
                        marginLeft: 8,
                        alignItems: 'center',
                      }}
                    >
                      <Text text={item?.topic?.title} variant="labelMedium" />
                      <ChevronRightRounded htmlColor={color.shade30} style={{ fontSize: 24 }} />
                    </View>
                  </TouchableOpacity>
                )
              }}
            />
          ) : uploadingPhoto || uploadingVideo ? (
            <div
              onFocus={() => setOnFocusUpload(true)}
              onBlur={() => setOnFocusUpload(false)}
              style={{ flexDirection: 'column', display: 'flex' }}
            >
              <Controller
                control={control}
                render={({ onChange, value }) => (
                  <FileInput
                    isMobile={isMobile}
                    accept="video/mpeg;video/mp4;video/quicktime;image/png;image/jpeg;image/gif"
                    style={{
                      paddingTop: 15,
                      paddingBottom: 15,
                      borderColor: onFocusUpload ? 'blue' : 'rgba(0, 0, 0, 0.3)',
                      borderWidth: onFocusUpload ? 2 : 1,
                    }}
                    value={value}
                    mainText={uploadingPhoto ? 'image' : 'video'}
                    helperText={
                      uploadingPhoto
                        ? 'We accept JPGs, PNGs, and GIFs up to 5mb'
                        : 'We accept MOV and MP4'
                    }
                    onChange={(file) => {
                      onChange(file)
                    }}
                  />
                )}
                name="media"
                defaultValue=""
              />
              {media && uploadingPhoto ? (
                <img
                  style={{
                    objectFit: 'contain',
                    alignSelf: 'center',
                    borderRadius: 20,
                    marginTop: 20,
                  }}
                  src={URL.createObjectURL(media)}
                  height="300"
                  width="300"
                  alt="uploaded"
                />
              ) : (
                media &&
                uploadingVideo && (
                  <video
                    style={{ alignSelf: 'center', marginTop: 50 }}
                    autoPlay={false}
                    height={200}
                    playsInline
                    disablePictureInPicture
                    controls
                    muted
                    src={URL.createObjectURL(media)}
                  />
                )
              )}
            </div>
          ) : (
            <View
              style={{
                flex: 1,
                justifyContent: 'space-between',
              }}
            >
              <View
                style={{
                  flex: 1,
                  justifyContent: 'flex-start',
                  flexDirection: 'column',
                  paddingTop: spacing[1],
                  // paddingBottom: spacing[7],
                }}
              >
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel="Choose topic"
                  onPress={() => setChoosingTopic(true)}
                  disabled={disableSelectTopic}
                >
                  <View
                    style={{
                      paddingTop: spacing[1],
                    }}
                  >
                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginVertical: spacing[3],
                        alignItems: 'center',
                      }}
                    >
                      {formData?.topic ? (
                        <>
                          <SvgIcon src={iconQuiz} color={color.black} size={24} />
                          <Text
                            variant="labelMedium"
                            text={formData?.topic?.topic.title}
                            style={{ marginLeft: spacing[2] }}
                          />
                        </>
                      ) : (
                        <>
                          <SvgIcon src={iconTopic} color={color.black} size={24} />
                          <Text
                            variant="labelMedium"
                            tx="Discuss.chooseTopic"
                            style={{ marginLeft: spacing[2] }}
                          />
                        </>
                      )}
                      <SvgIcon src={iconArrowFullDown} color={color.black} size={24} />
                    </View>
                  </View>
                </TouchableOpacity>
                <View
                  style={{
                    paddingVertical: spacing[4],
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    onChange={(e) => {
                      setValue('title', e.target.value)
                      setFormData({ ...formData, title: e.target.value })
                    }}
                    value={formData?.title}
                    placeholderTextColor={color.darkenGrey}
                    inputStyle={{
                      fontWeight: '600',
                      paddingTop: 9,
                      paddingLeft: 9,
                      paddingBottom: 9,
                      marginRight: 20,
                    }}
                    placeholder={t('Discuss.postRequired')}
                    style={{
                      color: '#000000',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      fontSize: 17,
                      paddingBottom: 0,
                    }}
                  />
                </View>
                <TextField
                  variant="outlined"
                  multiline
                  rows={6}
                  onChange={(e) => {
                    setValue('text', e.target.value)
                    setFormData({ ...formData, text: e.target.value })
                  }}
                  value={formData?.text}
                  placeholder={t('Discuss.textOptional')}
                  inputStyle={{
                    minHeight: 160,
                    marginBottom: 20,
                    paddingTop: 9,
                    paddingLeft: 9,
                  }}
                  style={{
                    color: '#000000',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    fontSize: 17,
                    paddingBottom: 0,
                  }}
                />
                {formData.photos?.length > 0 && (
                  <FlatList
                    horizontal
                    style={{
                      minHeight: 200,
                      marginBottom: 32,
                      backgroundColor: '#fff',
                    }}
                    data={formData.photos}
                    keyExtractor={(item, index) => index.toString()}
                    contentContainerStyle={{ paddingTop: 30 }}
                    showsHorizontalScrollIndicator={false}
                    renderItem={({ item, index }) => {
                      return (
                        <div style={{ marginRight: 20 }}>
                          <TouchableOpacity
                            accessibilityRole="button"
                            accessibilityLabel="Remove photo"
                            onPress={() => removePhoto(index)}
                            style={{ position: 'absolute', zIndex: 999, top: -10, right: 10 }}
                          >
                            <SvgIcon src={iconCancel} color="#000" />
                          </TouchableOpacity>
                          <img
                            src={URL.createObjectURL(item)}
                            height="174"
                            width="174"
                            style={{ objectFit: 'cover', borderRadius: 20 }}
                            alt="uploaded"
                          />
                        </div>
                      )
                    }}
                  />
                )}
                {renderUploadedVideo}
                <div
                  style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }}
                >
                  <View
                    style={{
                      borderTopWidth: formData.photos?.length > 0 ? 1 : 0,
                      borderTopColor: color.palette.mercury,
                      paddingVertical: 12,
                      flexDirection: 'row',
                      backgroundColor: '#fff',
                    }}
                  >
                    <TouchableOpacity
                      accessibilityRole="button"
                      accessibilityLabel="Upload photo"
                      onPress={() => setUploadingPhoto(true)}
                    >
                      <SvgIcon src={iconPhoto} color={color.black} size={24} />
                    </TouchableOpacity>
                    <View style={{ width: 20 }} />
                    <TouchableOpacity
                      accessibilityRole="button"
                      accessibilityLabel="Upload video"
                      onPress={() => setUploadingVideo(true)}
                      style={{ bottom: 2 }}
                    >
                      <SvgIcon src={iconVideoTalk} color={color.black} size={24} />
                    </TouchableOpacity>
                  </View>
                </div>
              </View>
            </View>
          )}
        </View>
      </DialogContent>
      <DialogActions
        style={{
          flexDirection: 'column',
          paddingTop: 20,
          paddingRight: 20,
          paddingBottom: 20,
          paddingLeft: 20,
        }}
      >
        {!choosingTopic && !uploadingPhoto && !uploadingVideo && (
          <MuiButton
            classes={classes}
            variant="contained"
            color="primary"
            disableElevation
            disabled={!formData?.topic || !formData?.title}
            onClick={(e) => {
              e.stopPropagation()

              createPost(formData)
              handleClose()
            }}
            style={{ width: '100%' }}
          >
            <Text variant="body1Emphasized" tx="Discuss.post" style={{ color: color.white }} />
          </MuiButton>
        )}
        {(uploadingPhoto || uploadingVideo) && (
          <MuiButton
            classes={classes}
            variant="contained"
            color="primary"
            disableElevation
            disabled={!media}
            onClick={(e) => {
              e.stopPropagation()
              if (uploadingPhoto) {
                const photos = [] as any
                if (formData.photos) {
                  photos.push(...formData.photos)
                }
                photos.push(media)
                setFormData({ ...formData, photos })
                formStore.setVideo(false)
                setUploadingPhoto(false)
              } else if (uploadingVideo) {
                setFormData({ ...formData, photos: [] })
                formStore.setVideo(media)
                setUploadingVideo(false)
              }
            }}
            style={{ width: '100%' }}
          >
            <Text
              variant="body1Emphasized"
              tx={uploadingVideo ? 'Discuss.uploadVideo' : 'Discuss.upload'}
              style={{ color: color.white }}
            />
          </MuiButton>
        )}
      </DialogActions>
    </Dialog>
  )
})
